import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import { useLingui } from "@lingui/react/macro";
import {
  Info,
  Plus,
  Check,
  Loader2,
} from "lucide-react";
import { FileUploader } from "@/components/common/FileUploader";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import type { UploadedFileItem } from "@/components/common/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { richTextPlain } from "@/lib/rich-text";
import { useUnits } from "@/hooks/useUnits";
import {
  allUnits,
  deriveServings,
  unitsCompatibleWith,
  withCurrentUnit,
} from "@/lib/units/unit-options";
import type { ApiFieldErrors } from "@/lib/api/api";
import {
  useRequirementNotes,
  type PublishRequirement,
} from "@/lib/recipes/publish-requirements";
import type { Recipe } from "@/types/recipe";
import type { UnitFamilyGroup } from "@/types/unit";

interface GeneralDetailsFormProps {
  recipe: Recipe;
  onSave?: (data: Partial<Recipe>) => void;
  isSaving?: boolean;
  canManage: boolean;
  /**
   * Which inputs the last save was rejected over, keyed by field name (T-704).
   * Before this the form showed one anonymous failure toast, so a cook was
   * told a required field was wrong without being told which.
   */
  fieldErrors?: ApiFieldErrors;
  /** What the recipe still needs before it can be published. */
  missingForPublish?: PublishRequirement[];
  /**
   * Whether the form holds edits that are not on the recipe yet, so the action
   * bar can offer to save them and the page can stop a cook walking away from
   * them (T-707).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface GeneralDetailsFormHandle {
  save: () => void;
  /** Put every box back to what the recipe says — the bar's Cancel. */
  reset: () => void;
}

const BACKGROUND_COLORS = [
  { id: "none", color: null },
  { id: "black", color: "#000000" },
  { id: "purple", color: "#6867af" },
  { id: "teal", color: "#16afd0" },
  { id: "pink", color: "#e16e79" },
  { id: "blue", color: "#4154d6" },
  { id: "peach", color: "#fddbca" },
  { id: "red", color: "#ea3e23" },
  { id: "blush", color: "#f6d3ce" },
  { id: "silver", color: "#e3e4e5" },
  { id: "rose", color: "#fcdde5" },
  { id: "slate", color: "#7a7b80" },
  { id: "fog", color: "#bebfc5" },
  { id: "forest", color: "#027974" },
  { id: "mint", color: "#6ac4a2" },
];

/** A number box holds text; an unset number is an empty box, not "null". */
function numberField(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

function parseNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The units a yield can be stated in. Containers are excluded: a case is
 * however much one ingredient's pack size says it is, so it cannot divide into
 * portions.
 */
function measurableUnits(groups: UnitFamilyGroup[]): string[] {
  return allUnits(groups.filter((group) => group.family !== "container"));
}

/** The two unit pickers, plain selects so the value is the whole control. */
function UnitSelect({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline-none disabled:bg-(--color-surface-muted) disabled:text-(--color-text-secondary)"
    >
      <option value="">{"\u2014"}</option>
      {options.map((unit) => (
        <option key={unit} value={unit}>
          {unit}
        </option>
      ))}
    </select>
  );
}

/** The asterisk on a field that has to be filled in before publishing. */
function RequiredMark() {
  const { t } = useLingui();
  return (
    <span className="text-(--color-danger)" title={t`Required to publish`} aria-hidden>
      {" *"}
    </span>
  );
}

/**
 * What to say under an input: the server's rejection if there is one, and
 * otherwise a reminder that publishing needs this box filled in. A rejection
 * wins — it is about the value that is there, not about the one that is not.
 */
function FieldNote({ errors, note }: { errors?: string[]; note?: string }) {
  if (errors && errors.length > 0) {
    return (
      <p role="alert" className="text-sm text-(--color-danger)">
        {errors.join(" ")}
      </p>
    );
  }
  if (note) {
    return <p className="text-sm text-(--color-text-secondary)">{note}</p>;
  }
  return null;
}

function FormDivider() {
  return <div className="h-px w-full bg-(--shell-border)" />;
}

function FormRow({
  label,
  description,
  required,
  children,
}: {
  label: string;
  description?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-8 items-start">
      <div className="flex w-[340px] shrink-0 flex-col gap-1">
        <p className="text-sm font-medium text-(--color-text-primary) leading-5">
          {label}
          {required && <RequiredMark />}
        </p>
        {description && (
          <p className="text-sm text-(--color-text-secondary) leading-5">{description}</p>
        )}
      </div>
      <div className="flex flex-[1_0_0] max-w-[600px]">{children}</div>
    </div>
  );
}

const inputClass = "";

export const GeneralDetailsForm = forwardRef<GeneralDetailsFormHandle, GeneralDetailsFormProps>(
function GeneralDetailsForm({
  recipe,
  onSave,
  isSaving = false,
  onDirtyChange,
  canManage,
  fieldErrors,
  missingForPublish = [],
}: GeneralDetailsFormProps, ref) {
  const { t } = useLingui();
  const [name, setName] = useState(recipe.name);
  const [description, setDescription] = useState(recipe.description ?? "");
  const [includeBackground, setIncludeBackground] = useState(false);
  const [selectedBgColor, setSelectedBgColor] = useState<string | null>(null);
  // Yield and portion are what the recipe records; the servings count is what
  // dividing them gives (T-706). Before this they were four boxes of local
  // state with nowhere to save to, which is why a yield never survived a
  // refresh — it was never sent, and there was no column to hold it.
  const [totalYield, setTotalYield] = useState(numberField(recipe.yieldQuantity));
  const [yieldUnit, setYieldUnit] = useState(recipe.yieldUom ?? "");
  const [portionSize, setPortionSize] = useState(numberField(recipe.portionQuantity));
  const [portionUnit, setPortionUnit] = useState(recipe.portionUom ?? "");
  const [profilePhotoFiles, setProfilePhotoFiles] = useState<UploadedFileItem[]>([]);
  const [backgroundMediaFiles, setBackgroundMediaFiles] = useState<UploadedFileItem[]>([]);

  const { groups } = useUnits();
  // A portion has to be measured the same way as the batch it comes out of, so
  // the second picker narrows to the first one's family. Whatever the recipe
  // already has stays selectable even if it is not on offer.
  const yieldUnitOptions = useMemo(
    () => withCurrentUnit(measurableUnits(groups), yieldUnit),
    [groups, yieldUnit],
  );
  const portionUnitOptions = useMemo(
    () =>
      withCurrentUnit(
        yieldUnit ? unitsCompatibleWith(groups, yieldUnit) : measurableUnits(groups),
        portionUnit,
      ),
    [groups, yieldUnit, portionUnit],
  );
  const servings = deriveServings(
    groups,
    { quantity: parseNumber(totalYield), unit: yieldUnit },
    { quantity: parseNumber(portionSize), unit: portionUnit },
  );

  // A requirement's note stays up until the recipe is saved and reloaded, but
  // it is dropped as soon as its boxes are filled in, so it does not sit under
  // a value the cook has just typed.
  const notes = useRequirementNotes();
  const stillMissing = (requirement: PublishRequirement, filledIn: boolean) =>
    !filledIn && missingForPublish.includes(requirement) ? notes[requirement] : undefined;
  const yieldNote = stillMissing("yield", parseNumber(totalYield) !== null && yieldUnit !== "");

  /**
   * What the recipe says right now, written the way the form writes it.
   * Comparing against this is how the action bar knows whether there is
   * anything to save, and how Cancel knows what to put back (T-707).
   */
  const pristine = useMemo(
    () => ({
      name: recipe.name,
      description: recipe.description ?? "",
      totalYield: numberField(recipe.yieldQuantity),
      yieldUnit: recipe.yieldUom ?? "",
      portionSize: numberField(recipe.portionQuantity),
      portionUnit: recipe.portionUom ?? "",
    }),
    [recipe],
  );

  const isDirty =
    name !== pristine.name ||
    description !== pristine.description ||
    totalYield !== pristine.totalYield ||
    yieldUnit !== pristine.yieldUnit ||
    portionSize !== pristine.portionSize ||
    portionUnit !== pristine.portionUnit;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const MAX_DESC = 275;
  // Counts what the reader sees, not the markup the editor saves.
  const charsLeft = MAX_DESC - richTextPlain(description).length;
  // The editor can't cut formatted text off at the limit, so an over-long
  // description is blocked at save instead.
  const descriptionTooLong = charsLeft < 0;
  const charsOver = -charsLeft;

  /**
   * Everything the general tab owns, sent together. The yield and the portion
   * go as nulls when their boxes are empty, so clearing one clears it on the
   * recipe rather than leaving the old number behind.
   */
  const payload = (overrides: Partial<Recipe> = {}): Partial<Recipe> => ({
    name,
    description,
    // Only sent when a photo was picked here. Echoing back the recipe's own
    // imageUrl sent the API the signed, hour-long URL it had just handed out,
    // which is far longer than the column holds — every save from this tab was
    // rejected as a result, whichever box had actually been edited.
    ...(profilePhotoFiles[0]?.imageUrl ? { imageUrl: profilePhotoFiles[0].imageUrl } : {}),
    yieldQuantity: parseNumber(totalYield),
    yieldUom: yieldUnit || null,
    portionQuantity: parseNumber(portionSize),
    portionUom: portionUnit || null,
    ...overrides,
  });

  const handleSave = () => {
    if (descriptionTooLong) {
      toast.error(t`Shorten the description to ${MAX_DESC} characters or fewer to save.`);
      return;
    }
    onSave?.(payload());
  };

  const reset = () => {
    setName(pristine.name);
    setDescription(pristine.description);
    setTotalYield(pristine.totalYield);
    setYieldUnit(pristine.yieldUnit);
    setPortionSize(pristine.portionSize);
    setPortionUnit(pristine.portionUnit);
  };

  useImperativeHandle(ref, () => ({ save: handleSave, reset }));

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Section header */}
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">
              {t`General Details`}
            </h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`Varius volutpat mi in vitae egestas facilisi viverra non.`}
            </p>
          </div>
        </div>
        <FormDivider />
      </div>

      {/* Secondary save control stays reachable near the top while editing long forms. */}
      {canManage && (
        <div className="sticky top-4 z-20 flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || isSaving || descriptionTooLong}
            className="h-11 gap-2 rounded-[0.625rem] bg-(--color-action) px-5 text-base font-medium text-(--color-action-fg) hover:bg-(--color-action)/90"
          >
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {isSaving ? t`Saving...` : t`Save`}
          </Button>
        </div>
      )}

      {/* Form fields */}
      <div className="flex flex-col gap-5">
        {/* Name */}
        <FormRow label={t`Name`}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={cn(inputClass, "w-full")}
            readOnly={!canManage}
          />
        </FormRow>
        <FormDivider />

        {/* Recipe profile photo */}
        <FormRow
          label={t`Recipe profile photo`}
          description={t`Pellentesque porta ut donec sc.`}
        >
          <div className="flex w-full items-start gap-5">
            <div className="size-16 shrink-0 rounded-[12px] bg-(--color-surface-accent) overflow-hidden flex items-center justify-center">
              {(profilePhotoFiles[0]?.previewUrl ?? recipe.imageUrl) ? (
                <img src={profilePhotoFiles[0]?.previewUrl ?? recipe.imageUrl} alt="" className="size-full object-cover" />
              ) : (
                <div className="size-full bg-(--color-surface-accent)" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <FileUploader
                module="recipe"
                multiple={false}
                maxFiles={1}
                hint={t`SVG, PNG, JPG or GIF (max. 800x400px)`}
                disabled={!canManage}
                onUploadedItemsChange={setProfilePhotoFiles}
              />
            </div>
          </div>
        </FormRow>
        <FormDivider />

        {/* Background media */}
        <FormRow
          label={t`Background media`}
          description={t`Elerisque ac consectetur ut pellentesque.`}
        >
          <div className="flex flex-col gap-5 w-full">
            {/* Toggle */}
            <div className="flex items-start gap-2">
              <Button
                variant="ghost"
                type="button"
                role="switch"
                aria-checked={includeBackground}
                onClick={() => setIncludeBackground((v) => !v)}
                className={cn(
                  "relative inline-flex justify-start h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors p-0 hover:bg-transparent",
                  includeBackground ? "bg-(--color-action)" : "bg-(--color-surface-accent)",
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block size-5 rounded-full bg-(--color-surface-content) shadow-lg transition-transform",
                    includeBackground ? "translate-x-5" : "translate-x-0",
                  )}
                />
              </Button>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-(--color-text-primary) leading-none">
                    {t`Include background media`}
                  </span>
                  <Info className="size-5 text-(--color-text-secondary)" />
                </div>
                <p className="text-sm text-(--color-text-secondary)">
                  {t`Add a background image or video displayed as the recipe's top header.`}
                </p>
              </div>
            </div>

            {includeBackground && (
              <>
                {/* Upload media */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-(--color-text-primary) leading-6">{t`Upload Your Media`}</p>
                  <div className="flex items-start gap-5">
                    <div className="size-16 shrink-0 rounded-[12px] bg-(--color-surface-accent) overflow-hidden flex items-center justify-center">
                      {backgroundMediaFiles[0]?.previewUrl && (
                        <img src={backgroundMediaFiles[0].previewUrl} alt="" className="size-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <FileUploader
                        module="recipe"
                        multiple={false}
                        maxFiles={1}
                        hint={t`PNG, JPG. Max file size: 10 MB`}
                        disabled={!canManage}
                        onUploadedItemsChange={setBackgroundMediaFiles}
                      />
                    </div>
                  </div>
                </div>

                {/* Solid background color picker */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-(--color-text-primary) leading-6">{t`Solid Background`}</p>
                  <div className="flex flex-wrap gap-2">
                    {BACKGROUND_COLORS.map(({ id, color }) => {
                      const isSelected = selectedBgColor === id;
                      if (!color) {
                        return (
                          <Button
                            key={id}
                            variant="ghost"
                            type="button"
                            onClick={() => setSelectedBgColor(id)}
                            className={cn(
                              "flex size-10 items-center justify-center rounded-[8px] transition-colors p-0",
                              isSelected
                                ? "bg-(--color-surface-accent) border-2 border-(--color-border-strong) hover:bg-(--color-surface-accent)"
                                : "bg-(--color-surface-accent) border-2 border-(--color-border-strong) hover:bg-(--color-surface-accent)",
                            )}
                          >
                            <Check className="size-5 text-(--color-text-primary)" />
                          </Button>
                        );
                      }
                      return (
                        <Button
                          key={id}
                          variant="ghost"
                          type="button"
                          onClick={() => setSelectedBgColor(id)}
                          style={{ backgroundColor: color }}
                          className={cn(
                            "size-10 rounded-[8px] transition-opacity p-0 hover:opacity-90",
                            isSelected && "ring-2 ring-offset-1 ring-(--brand-black)",
                          )}
                          aria-label={id}
                        />
                      );
                    })}
                    {/* Custom color */}
                    <Button
                      variant="ghost"
                      type="button"
                      className="flex size-10 items-center justify-center rounded-[8px] border border-dashed border-(--color-border-strong) bg-(--color-surface-content) hover:bg-(--color-surface-muted) transition-colors p-0"
                      aria-label={t`Custom color`}
                    >
                      <Plus className="size-5 text-(--color-text-secondary)" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </FormRow>
        <FormDivider />

        {/* Short dish description */}
        <FormRow label={t`Short dish description`} description={t`Write a short introduction.`}>
          <div className="flex flex-col gap-3 w-full">
            <div className="flex flex-col gap-1.5">
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder={t`Enter some description...`}
                readOnly={!canManage}
              />
              {descriptionTooLong ? (
                <p role="alert" className="text-sm text-(--color-danger)">
                  {t`${charsOver} characters over the ${MAX_DESC} limit`}
                </p>
              ) : (
                <p className="text-sm text-(--color-text-secondary)">
                  {`${charsLeft} ${t`characters left`}`}
                </p>
              )}
            </div>
          </div>
        </FormRow>
        <FormDivider />

        {/* Yield & Portioning */}
        <FormRow
          label={t`Yield & Portioning`}
          description={t`Defines scaling, cost-per-portion, and batch planning.`}
          required
        >
          <div className="flex flex-col gap-3 w-full">
            {/* Total yield row */}
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-2">
                <Label className="text-sm text-(--color-text-primary)">
                  {t`Total yield`}
                  <RequiredMark />
                </Label>
                <Input
                  type="number"
                  aria-label={t`Total yield`}
                  value={totalYield}
                  onChange={(e) => setTotalYield(e.target.value)}
                  className={inputClass}
                  readOnly={!canManage}
                />
              </div>
              <div className="flex w-[120px] flex-col gap-2">
                <Label className="text-sm text-(--color-text-primary)">{t`Unit`}</Label>
                <UnitSelect
                  label={t`Yield unit`}
                  value={yieldUnit}
                  options={yieldUnitOptions}
                  onChange={(next) => {
                    setYieldUnit(next);
                    // A portion measured the old way no longer belongs to the
                    // batch, so it is dropped rather than left to be rejected.
                    if (
                      portionUnit &&
                      !unitsCompatibleWith(groups, next).includes(portionUnit)
                    ) {
                      setPortionUnit("");
                    }
                  }}
                  disabled={!canManage}
                />
              </div>
            </div>
            <FieldNote
              errors={fieldErrors?.yieldQuantity ?? fieldErrors?.yieldUom}
              note={yieldNote}
            />

            {/* Portion size row */}
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-2">
                <Label className="text-sm text-(--color-text-primary)">
                  {t`Portion size`}
                </Label>
                <Input
                  type="number"
                  aria-label={t`Portion size`}
                  value={portionSize}
                  onChange={(e) => setPortionSize(e.target.value)}
                  className={inputClass}
                  readOnly={!canManage}
                />
              </div>
              <div className="flex w-[120px] flex-col gap-2">
                <Label className="text-sm text-(--color-text-primary)">{t`Unit`}</Label>
                <UnitSelect
                  label={t`Portion unit`}
                  value={portionUnit}
                  options={portionUnitOptions}
                  onChange={setPortionUnit}
                  disabled={!canManage}
                />
              </div>
            </div>
            <FieldNote
              errors={fieldErrors?.portionQuantity ?? fieldErrors?.portionUom}
            />

            {/* Servings — the quotient of the two rows above, not a field. */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <Label className="text-sm text-(--color-text-primary)">{t`Servings`}</Label>
                <Info className="size-5 text-(--color-text-secondary)" />
              </div>
              <Input
                aria-label={t`Servings`}
                value={servings === null ? "" : String(servings)}
                placeholder={t`Set a yield and a portion size`}
                className={cn(inputClass, "w-full bg-(--color-surface-muted)")}
                readOnly
              />
              <p className="text-sm text-(--color-text-secondary)">
                {servings === null
                  ? t`Servings is worked out from the yield and the portion size — 128 fl oz yield ÷ 1 fl oz portion = 128 servings.`
                  : `${totalYield} ${yieldUnit} ÷ ${portionSize} ${portionUnit} = ${servings} ${t`servings`}`}
              </p>
            </div>
          </div>
        </FormRow>
      </div>
    </div>
  );
});
